import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Button, Alert, Badge, Table, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

interface ReferralStats {
  referralCode: string;
  referredUsers: Array<{
    _id: string;
    name: string;
    email: string;
    createdAt: string;
  }>;
  totalReferrals: number;
  completedReferrals?: number;
  totalReferralReward?: number;
  referralRewardCount: number;
  level1Earnings?: number;
  lowerLevelEarnings?: number;
}

interface Transaction {
  _id: string;
  type: string;
  amount: number;
  description: string;
  createdAt: string;
  status: string;
}

interface TotalEarnings {
  totalEarnings: number;
  totalWithdrawals: number;
  currentBalance: number;
  earningTransactionCount: number;
  withdrawalCount: number;
}

const UserDashboard: React.FC = () => {
  const { user, refreshUser } = useAuth();
  const [referralStats, setReferralStats] = useState<ReferralStats | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [totalEarnings, setTotalEarnings] = useState<TotalEarnings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      
      console.log('Fetching dashboard data...');
      const [referralResponse, transactionResponse, earningsResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/users/referrals`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }),
        axios.get(`${API_BASE_URL}/users/rewards/transactions?limit=10`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }),
        axios.get(`${API_BASE_URL}/users/total-earnings`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        })
      ]);

      if (referralResponse.data.success) {
        setReferralStats(referralResponse.data);
      }

      if (transactionResponse.data.success) {
        console.log('Transactions received:', transactionResponse.data.transactions);
        setTransactions(transactionResponse.data.transactions);
      } else {
        console.log('Transaction API failed:', transactionResponse.data);
      }

      if (earningsResponse.data.success) {
        console.log('Total earnings received:', earningsResponse.data);
        setTotalEarnings(earningsResponse.data);
      } else {
        console.log('Earnings API failed:', earningsResponse.data);
      }
    } catch (error) {
      console.error('Dashboard data fetch error:', error);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL]);

  useEffect(() => {
    // Refresh user data once on mount for latest balance
    refreshUser();
    fetchDashboardData();
  }, []);

  const [copiedToClipboard, setCopiedToClipboard] = useState(false);

  const shareReferralCode = () => {
    if (!referralStats?.referralCode) {
      alert('Referral code not available');
      return;
    }

    const shareText = `Join NetworqPay and earn rewards! Use my referral code: ${referralStats.referralCode}`;
    const shareUrl = `${window.location.origin}/register?ref=${referralStats.referralCode}`;
    
    if (navigator.share) {
      navigator.share({
        title: 'Join NetworqPay',
        text: shareText,
        url: shareUrl,
      }).catch((error) => {
        console.error('Error sharing:', error);
        fallbackShare(shareText, shareUrl);
      });
    } else {
      fallbackShare(shareText, shareUrl);
    }
  };

  const fallbackShare = (shareText: string, shareUrl: string) => {
    // For desktop/browsers without native share
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(shareText + ' - ' + shareUrl)}`;
    window.open(whatsappUrl, '_blank');
  };

  const copyReferralCode = async () => {
    if (!referralStats?.referralCode) {
      alert('Referral code not available');
      return;
    }
    
    try {
      // Try modern clipboard API first
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(referralStats.referralCode);
      } else {
        // Fallback for older browsers or non-secure contexts
        const textArea = document.createElement('textarea');
        textArea.value = referralStats.referralCode;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      setCopiedToClipboard(true);
      setTimeout(() => setCopiedToClipboard(false), 2000);
    } catch (error) {
      console.error('Failed to copy referral code:', error);
      alert('Failed to copy referral code. Please copy it manually.');
    }
  };

  const getTransactionIcon = (type: string) => {
    switch (type.toLowerCase()) {
      case 'referral':
        return 'fas fa-user-plus text-success';
      case 'task':
        return 'fas fa-check-circle text-primary';
      case 'bonus':
        return 'fas fa-gift text-warning';
      default:
        return 'fas fa-coins text-info';
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return <Badge bg="success">Completed</Badge>;
      case 'pending':
        return <Badge bg="warning">Pending</Badge>;
      case 'failed':
        return <Badge bg="danger">Failed</Badge>;
      default:
        return <Badge bg="secondary">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{minHeight: '400px'}}>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <>
      {/* Header */}
      <Row className="mb-4">
        <Col>
          <h1 className="h3 mb-1">Welcome back, {user?.name}!</h1>
          <p className="text-muted mb-0">Here's your activity overview and recent transactions.</p>
        </Col>
      </Row>

      {/* Error alerts hidden for cleaner UI */}
      {/* {error && (
        <Alert variant="danger" className="mb-4">
          <i className="fas fa-exclamation-triangle me-2"></i>
          {error}
        </Alert>
      )} */}

      {/* Statistics Cards */}
      <Row className="mb-4">
        {/* Total Earnings */}
        <Col xl={3} md={6} className="mb-3">
          <div className="admin-stats-card" style={{backgroundColor: '#d4edda', borderColor: '#c3e6cb'}}>
            <i className="fas fa-coins admin-stats-icon text-success"></i>
            <div className="admin-stats-number text-success">₹{totalEarnings?.totalEarnings?.toFixed(2) || '0.00'}</div>
            <div className="admin-stats-label text-success">Total Earnings</div>
          </div>
        </Col>
        {/* Current Balance */}
        <Col xl={3} md={6} className="mb-3">
          <div className="admin-stats-card" style={{backgroundColor: '#fff3cd', borderColor: '#ffeaa7'}}>
            <i className="fas fa-wallet admin-stats-icon text-warning"></i>
            <div className="admin-stats-number text-warning">₹{user?.rewardBalance?.toFixed(2) || '0.00'}</div>
            <div className="admin-stats-label text-warning">Current Balance</div>
          </div>
        </Col>
        <Col xl={3} md={6} className="mb-3">
          <div className="admin-stats-card" style={{backgroundColor: '#fff3cd', borderColor: '#ffeaa7'}}>
            <i className="fas fa-users admin-stats-icon text-warning"></i>
            <div className="admin-stats-number text-warning">₹{referralStats?.totalReferralReward?.toFixed(2) || '0.00'}</div>
            <div className="admin-stats-label text-warning">Referral Earnings</div>
          </div>
        </Col>
        <Col xl={3} md={6} className="mb-3">
          <div className="admin-stats-card" style={{backgroundColor: '#f8d7da', borderColor: '#f1aeb5'}}>
            <i className="fas fa-money-bill-wave admin-stats-icon text-danger"></i>
            <div className="admin-stats-number text-danger">₹{totalEarnings?.totalWithdrawals?.toFixed(2) || '0.00'}</div>
            <div className="admin-stats-label text-danger">Total Withdrawals</div>
          </div>
        </Col>
        <Col xl={3} md={6} className="mb-3">
          <div className="admin-stats-card info">
            <i className="fas fa-chart-line admin-stats-icon"></i>
            <div className="admin-stats-number">{transactions.length}</div>
            <div className="admin-stats-label">Total Transactions</div>
          </div>
        </Col>
      </Row>

      {/* Main Content Row */}
      <Row>
        {/* Referral Section */}
        <Col lg={4} className="mb-4">
          <div className="admin-card">
            <div className="admin-card-header">
              <h5 className="mb-0">
                <i className="fas fa-share-alt me-2"></i>
                Referral Program
              </h5>
            </div>
            <div className="admin-card-body">
              <div className="text-center mb-3">
                <div className="referral-code-display p-3 bg-light rounded">
                  <div className="small text-muted mb-1">Your Referral Code</div>
                  <div className="h4 mb-2 font-monospace text-primary">{referralStats?.referralCode || 'N/A'}</div>
                  <Button 
                    variant="outline-primary" 
                    size="sm"
                    onClick={copyReferralCode}
                  >
                    <i className={`fas ${copiedToClipboard ? 'fa-check text-success' : 'fa-copy'} me-1`}></i>
                    {copiedToClipboard ? 'Copied!' : 'Copy Code'}
                  </Button>
                </div>
              </div>
              
              <div className="referral-stats">
                <div className="d-flex justify-content-between mb-2">
                  <span>Total Referrals:</span>
                  <strong className="text-success">{referralStats?.totalReferrals || 0}</strong>
                </div>
                <div className="d-flex justify-content-between mb-2">
                  <span>Active Referrals:</span>
                  <strong className="text-info">{referralStats?.completedReferrals || 0}</strong>
                </div>
                <div className="d-flex justify-content-between mb-3">
                  <span>Total Earnings:</span>
                  <strong className="text-primary">₹{referralStats?.totalReferralReward?.toFixed(2) || '0.00'}</strong>
                </div>
              </div>

              <Button 
                variant="primary" 
                className="w-100 mt-3"
                onClick={shareReferralCode}
              >
                <i className="fas fa-share me-1"></i>
                Share Referral Code
              </Button>
            </div>
          </div>
        </Col>

        {/* Recent Transactions */}
        <Col lg={8} className="mb-4">
          <div className="admin-card">
            <div className="admin-card-header d-flex justify-content-between align-items-center">
              <h5 className="mb-0">
                <i className="fas fa-history me-2"></i>
                Recent Transactions
              </h5>
              <Link to="/app/transactions" className="btn btn-outline-primary btn-sm">
                <i className="fas fa-eye me-1"></i>
                View All
              </Link>
            </div>
            <div className="admin-card-body p-0">
              <Table responsive className="admin-table mb-0">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Description</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.length > 0 ? (
                    transactions.map((transaction) => (
                      <tr key={transaction._id}>
                        <td>
                          <i className={getTransactionIcon(transaction.type)}></i>
                          <span className="ms-2">{transaction.type
                            .replace('referral_reward', 'Referral Reward')
                            .replace('upgrade_commission_L1', 'Upgrade Reward L1')
                            .replace('upgrade_commission_L2', 'Upgrade Reward L2')
                            .replace('upgrade_commission_L3', 'Upgrade Reward L3')
                            .replace('upgrade_commission_L4', 'Upgrade Reward L4')
                            .replace(/_/g, ' ')
                            .replace(/\b\w/g, l => l.toUpperCase())}
                          </span>
                        </td>
                        <td>{transaction.description}</td>
                        <td>
                          <span className={`fw-bold ${transaction.amount > 0 ? 'text-success' : 'text-danger'}`}>
                            {transaction.amount > 0 ? '+' : ''}₹{transaction.amount.toFixed(2)}
                          </span>
                        </td>
                        <td>{getStatusBadge(transaction.status)}</td>
                        <td className="small text-muted">
                          {new Date(transaction.createdAt).toLocaleDateString()}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5} className="text-center py-4 text-muted">
                        <i className="fas fa-inbox fa-2x mb-2"></i>
                        <p className="mb-0">No transactions yet</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </div>
        </Col>
      </Row>

      {/* Quick Actions & Recent Referrals */}
      <Row>
        <Col lg={8} className="mb-4">
          <div className="admin-card">
            <div className="admin-card-header">
              <h5 className="mb-0">
                <i className="fas fa-bolt me-2"></i>
                Quick Actions
              </h5>
            </div>
            <div className="admin-card-body">
              <Row>
                <Col md={3} sm={6} className="mb-3">
                  <Link to="/app/tasks" className="text-decoration-none">
                    <Button variant="outline-primary" className="w-100 admin-btn" style={{height: '60px'}}>
                      <i className="fas fa-tasks d-block mb-1"></i>
                      <small>Browse Tasks</small>
                    </Button>
                  </Link>
                </Col>
                <Col md={3} sm={6} className="mb-3">
                  <Link to="/app/referrals" className="text-decoration-none">
                    <Button variant="outline-success" className="w-100 admin-btn" style={{height: '60px'}}>
                      <i className="fas fa-share-alt d-block mb-1"></i>
                      <small>Invite Friends</small>
                    </Button>
                  </Link>
                </Col>
                <Col md={3} sm={6} className="mb-3">
                  <Link to="/app/transactions" className="text-decoration-none">
                    <Button variant="outline-warning" className="w-100 admin-btn" style={{height: '60px'}}>
                      <i className="fas fa-gift d-block mb-1"></i>
                      <small>View Rewards</small>
                    </Button>
                  </Link>
                </Col>
                <Col md={3} sm={6} className="mb-3">
                  <Link to="/app/profile" className="text-decoration-none">
                    <Button variant="outline-info" className="w-100 admin-btn" style={{height: '60px'}}>
                      <i className="fas fa-user d-block mb-1"></i>
                      <small>Edit Profile</small>
                    </Button>
                  </Link>
                </Col>
              </Row>
            </div>
          </div>
        </Col>

        <Col lg={4} className="mb-4">
          <div className="admin-card">
            <div className="admin-card-header">
              <h5 className="mb-0">
                <i className="fas fa-users me-2"></i>
                Recent Referrals
              </h5>
            </div>
            <div className="admin-card-body">
              {referralStats?.referredUsers && referralStats.referredUsers.length > 0 ? (
                referralStats.referredUsers.slice(0, 5).map((referral) => (
                  <div key={referral._id} className="d-flex align-items-center mb-3">
                    <div className="admin-user-avatar me-3" style={{width: '32px', height: '32px', fontSize: '0.75rem'}}>
                      {referral.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-grow-1">
                      <div className="fw-semibold">{referral.name}</div>
                      <small className="text-muted">{new Date(referral.createdAt).toLocaleDateString()}</small>
                    </div>
                    <Badge bg="success" className="small">New</Badge>
                  </div>
                ))
              ) : (
                <div className="text-center py-4 text-muted">
                  <i className="fas fa-user-plus fa-2x mb-2"></i>
                  <p className="mb-0">No referrals yet</p>
                  <small>Share your referral code to earn bonuses!</small>
                </div>
              )}
            </div>
          </div>
        </Col>
      </Row>
    </>
  );
};

export default UserDashboard;